import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import { isSocialCasinoLocale } from '@/utils/Locale'

const JACKPOT_UPGRADE_SPINE_SRC = 'jackpot_upgrade'
const JACKPOT_UPGRADE_SPINE_NAME = 'Jackpot Upgrade Spine'
const JACKPOT_UPGRADE_OVERLAY_NAME = 'Jackpot Upgrade Overlay'
const JACKPOT_UPGRADE_OVERLAY_OPACITY = 0.65
const JACKPOT_UPGRADE_SPINE_SKIN = isSocialCasinoLocale() ? 'prize_pool' : 'jackpot'

const JACKPOT_UPGRADE_IN = 'eng_in'
const JACKPOT_UPGRADE_LOOP = 'eng_loop'
const JACKPOT_UPGRADE_OUT = 'eng_out'

// Total presentation budget: eng_in (0.4s) + hold (1.1s) + eng_out (0.5s) = 2s.
const JACKPOT_UPGRADE_LOOP_HOLD_S = 1.1
// Hard cap per upgrade, so a missing spine completion callback can never stall the collect.
// Must stay below ReelsContainer.FS_COLLECT_UPGRADE_TIMEOUT_SECONDS so this fires first.
const JACKPOT_UPGRADE_MAX_DURATION_S = 8

let isJackpotUpgradePlaying = false

const findChildByName = (container: PIXI.Container | null, name: string): PIXI.Container | null => {
    if (!container) {
        return null
    }

    if ((container as any).name === name || (container as any).label === name) {
        return container
    }

    for (const child of container.children) {
        const match = findChildByName(child as PIXI.Container, name)
        if (match) {
            return match
        }
    }

    return null
}

//## skippable by tap or click
const setSkipInputEnabled = (container: PIXI.Container, enabled: boolean): void => {
    const overlay = findChildByName(container, JACKPOT_UPGRADE_OVERLAY_NAME)
    if (!overlay) {
        return
    }

    overlay.interactive = enabled
    overlay.eventMode = enabled ? 'static' : 'auto'

    // A hand cursor only means anything with a mouse.
    if (!DeviceUtils.isMobileDevice()) {
        overlay.cursor = enabled ? 'pointer' : 'default'
    }
}

// Plays one full upgrade presentation:
//   eng_in   -> reveal
//   eng_loop -> hold for JACKPOT_UPGRADE_LOOP_HOLD_S
//   eng_out  -> dismiss
const playJackpotUpgrade = (spine: SpineAnimation): Promise<void> =>
    new Promise<void>((resolve) => {
        let holdCall: gsap.core.Tween | null = null
        let safetyCall: gsap.core.Tween | null = null
        let settled = false
        let isPlayingOut = false
        let isPlayingLoop = false
        let skipRequested = false

        const finish = () => {
            if (settled) {
                return
            }
            settled = true

            holdCall?.kill()
            holdCall = null
            safetyCall?.kill()
            safetyCall = null
            ;(GameSignals as any).jackpotLadderUpgradeSkip?.disconnect(onSkip)

            resolve()
        }

        const playOut = () => {
            if (settled || isPlayingOut) {
                return
            }
            isPlayingOut = true

            holdCall?.kill()
            holdCall = null

            try {
                spine.start([JACKPOT_UPGRADE_OUT], [false], 0, [1], finish)
            } catch {
                finish()
            }
        }

        const onSkip = () => {
            if (settled || isPlayingOut) {
                return
            }

            skipRequested = true
            if (isPlayingLoop) {
                playOut()
            }
        }

        const startLoop = () => {
            if (settled) {
                return
            }

            if (skipRequested) {
                playOut()
                return
            }

            isPlayingLoop = true

            try {
                spine.start([JACKPOT_UPGRADE_LOOP], [true], 0, [1])
            } catch {
                // keep going even if the loop clip is momentarily unavailable
            }

            holdCall = gsap.delayedCall(JACKPOT_UPGRADE_LOOP_HOLD_S, playOut)
        }

        ;(GameSignals as any).jackpotLadderUpgradeSkip?.connect(onSkip)

        // Safety net: never let a missing spine callback stall the collect sequence.
        safetyCall = gsap.delayedCall(JACKPOT_UPGRADE_MAX_DURATION_S, finish)

        // SpineAnimation.stop() clears the selected skin, so restore it for every presentation.
        spine.setSkin(JACKPOT_UPGRADE_SPINE_SKIN)
        spine.visible = true
        try {
            spine.start([JACKPOT_UPGRADE_IN], [false], 0, [1], startLoop)
        } catch {
            startLoop()
        }
    })

const runJackpotUpgradeSequence = async (container: PIXI.Container): Promise<void> => {
    if (isJackpotUpgradePlaying) {
        ;(GameSignals as any).jackpotLadderUpgradeComplete?.emit()
        return
    }

    const spine = findChildByName(container, JACKPOT_UPGRADE_SPINE_NAME) as SpineAnimation | null

    if (!spine) {
        ;(GameSignals as any).jackpotLadderUpgradeComplete?.emit()
        return
    }

    isJackpotUpgradePlaying = true
    container.visible = true
    setSkipInputEnabled(container, true)

    await playJackpotUpgrade(spine)

    setSkipInputEnabled(container, false)

    try {
        spine.stop()
    } catch {
        // ignore stop errors during teardown
    }

    spine.visible = false
    container.visible = false
    isJackpotUpgradePlaying = false

    ;(GameSignals as any).jackpotLadderUpgradeComplete?.emit()
}

export const JACKPOT_UPGRADE_COMPONENT: IComponent = {
    name: ContainersNames.JACKPOT_UPGRADE_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 445],
        [Orientation.PORTRAIT, 445],
    ]),
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).jackpotLadderUpgradeStart],
            callback: (data: ICustomEventData) => {
                const container = data.component.componentElement as PIXI.Container
                void runJackpotUpgradeSequence(container)
            },
        },
    ],
    items: [
        {
            name: JACKPOT_UPGRADE_OVERLAY_NAME,
            type: AssetType.OVERLAY,
            componentStyle: {
                color: 0x01102a,
                opacity: JACKPOT_UPGRADE_OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        (GameSignals as any).jackpotLadderUpgradeStart,
                        ModuleSignals.gameResized,
                    ],
                    callback: (data: ICustomEventData) => {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            0x01102a,
                            JACKPOT_UPGRADE_OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: JACKPOT_UPGRADE_SPINE_NAME,
            type: AssetType.SPINE,
            visible: false,
            src: JACKPOT_UPGRADE_SPINE_SRC,
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: [JACKPOT_UPGRADE_IN],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            scale: [
                { orientation: Orientation.LANDSCAPE, x: 0.7, y: 0.7 },
                { orientation: Orientation.PORTRAIT, x: 0.7, y: 0.7 },
            ],
            position: [
                { orientation: Orientation.LANDSCAPE, x: 1050, y: 300 },
                { orientation: Orientation.PORTRAIT, x: 540, y: 840 },
            ],
        },
    ],
}
